import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { API_CONFIG } from '../../config/settings'

const POLL_MS = 3000
const POLL_MAX = 7

type Status = 'working' | 'ok' | 'stalled' | 'error'

export default function BookingPaymentReturn() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('working')
  const [message, setMessage] = useState('Confirming your AonlaStay payment…')
  const [bookingNumber, setBookingNumber] = useState<number | null>(null)
  const [reverifyTick, setReverifyTick] = useState(0)

  const payment = searchParams.get('payment')
  const orderNo = searchParams.get('order_no')
  const cfOrderId = searchParams.get('cf_order_id')
  const roomId = searchParams.get('room_id')
  const reasonRaw = searchParams.get('reason')

  const supportWa = useMemo(() => {
    const text = `Hi AonlaStay — I need help with a booking payment.\nOrder: ${orderNo || '—'}\nCashfree: ${cfOrderId || '—'}`
    return `https://wa.me/917817803342?text=${encodeURIComponent(text)}`
  }, [orderNo, cfOrderId])

  const roomLink = roomId ? `/room/${roomId}` : '/rooms'

  const handleReverify = useCallback(() => {
    setStatus('working')
    setMessage('Confirming your AonlaStay payment…')
    setReverifyTick((n) => n + 1)
  }, [])

  useEffect(() => {
    if (!orderNo || !cfOrderId) {
      setStatus('error')
      setMessage('Missing payment details. Please try booking again or contact support.')
      return
    }

    if ((payment === 'failed' || payment === 'error') && reverifyTick === 0) {
      let msg = 'Payment did not complete.'
      if (reasonRaw) {
        try {
          msg = decodeURIComponent(reasonRaw.replace(/\+/g, ' '))
        } catch {
          msg = reasonRaw
        }
      }
      setStatus('error')
      setMessage(msg)
      return
    }

    if (!['success', 'pending', 'failed', 'error'].includes(payment || '')) {
      setStatus('error')
      setMessage('Invalid payment return. Please try again from the room page.')
      return
    }

    let cancelled = false

    const verifyOnce = async () => {
      const res = await fetch(`${API_CONFIG.BASE_URL}/bookings/payment/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_no: orderNo, cf_order_id: cfOrderId }),
      })
      const json = await res.json().catch(() => ({}))
      return { res, json }
    }

    ;(async () => {
      let attempts = 0
      while (!cancelled && attempts < POLL_MAX) {
        attempts += 1
        try {
          const { res, json } = await verifyOnce()
          if (cancelled) return

          if (res.ok && json.ok) {
            const bn = Number(json.booking_number ?? orderNo)
            setBookingNumber(Number.isFinite(bn) ? bn : null)
            setStatus('ok')
            setMessage('Payment confirmed! Your stay is booked with AonlaStay.')
            sessionStorage.removeItem('aonlastay_pending_booking')
            return
          }

          if (json.status === 'pending') {
            if (attempts >= POLL_MAX) break
            await new Promise((r) => setTimeout(r, POLL_MS))
            continue
          }

          setStatus('error')
          setMessage(json.detail || json.error || 'Payment could not be confirmed.')
          return
        } catch (e) {
          if (!cancelled) {
            setStatus('error')
            setMessage(e instanceof Error ? e.message : 'Network error while verifying payment.')
          }
          return
        }
      }

      if (!cancelled) {
        setStatus('stalled')
        setMessage(
          'We could not confirm payment automatically. If money was deducted, WhatsApp us with your booking number and we will help.'
        )
      }
    })()

    return () => {
      cancelled = true
    }
  }, [payment, orderNo, cfOrderId, reasonRaw, reverifyTick])

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-lg w-full rounded-2xl border border-gray-200 bg-white p-8 shadow-lg text-center">
        {status === 'working' && (
          <div className="space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary-500 mx-auto" />
            <p className="text-gray-600">{message}</p>
          </div>
        )}

        {status === 'ok' && (
          <div className="space-y-5">
            <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">Booking confirmed</h1>
            <p className="text-gray-600 text-sm">{message}</p>
            {bookingNumber && (
              <p className="text-sm text-gray-500">
                Booking #<span className="font-semibold text-gray-800">{bookingNumber}</span>
              </p>
            )}
            <button
              type="button"
              onClick={() => navigate(roomLink, { replace: true })}
              className="w-full rounded-xl bg-primary-500 text-white py-3 font-semibold hover:bg-primary-600"
            >
              View your stay
            </button>
          </div>
        )}

        {(status === 'stalled' || status === 'error') && (
          <div className="space-y-5">
            <AlertCircle className="h-14 w-14 text-amber-500 mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">
              {status === 'stalled' ? 'Payment not confirmed yet' : 'Something went wrong'}
            </h1>
            <p className="text-gray-600 text-sm">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={handleReverify}
                className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold"
              >
                Reverify payment
              </button>
              <a
                href={supportWa}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-xl border border-green-500 text-green-700 text-sm font-semibold"
              >
                WhatsApp support
              </a>
            </div>
            <Link to={roomLink} className="inline-block text-sm text-primary-600 font-medium">
              ← Back to listings
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
